import React, { useState } from 'react';

import axios from 'axios';
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import Async from 'react-select/async';
import voca from 'voca';

import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';

import CheckboxField from 'components/application/CheckboxField';
import {
  ClearIndicator,
  NoOptionsMessage,
} from 'components/application/CollectionSelect';
import DatePickerField from 'components/application/DatePickerField';
import ErrorMessage from 'components/application/ErrorMessage';
import RadioField from 'components/application/RadioField';
import TextArea from 'components/application/TextArea';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import ProjectContentReviewFooter from 'components/projectReviews/ProjectContentReviewFooter';

import { formatDuration } from 'components/helpers/dates';
import { personDisplayName } from 'components/helpers/users';

function Option(props) {
  return (
    <components.Option {...props}>
      <div className='collection-select__option_container'>{props.label}</div>
    </components.Option>
  );
}

function AssignButton(user, handleReviewOptionChange) {
  const onClick = () => {
    handleReviewOptionChange(
      {
        value: {
          firstName: user.data.attributes.firstName,
          lastName: user.data.attributes.lastName,
          id: user.data.id,
        },
      },
      {
        name: 'assignedUser',
      },
    );
  };

  return (
    <button
      className='app-link tw-border-0 tw-bg-transparent tw-p-0 tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
      onClick={onClick}
    >
      {personDisplayName({ attributes: user.data.attributes })}
    </button>
  );
}

const scheduledContentReviewRequestDueIn = ({ i18nT, reviewDueDate }) => {
  const dueDate = moment(reviewDueDate).startOf('day');
  const tomorrow = moment().add(1, 'day').startOf('day');
  const now = moment().startOf('day');

  if (dueDate.isSame(now)) {
    return i18nT('completeReview.nextReviewDue.today');
  } else if (dueDate.isSame(tomorrow)) {
    return i18nT('completeReview.nextReviewDue.tomorrow');
  } else if (dueDate.isAfter(tomorrow)) {
    return i18nT('completeReview.nextReviewDue.date', {
      date: now.to(dueDate),
    });
  } else {
    return i18nT('completeReview.nextReviewDue.overdue');
  }
};

export default function ProjectContentReviewManager({
  isReviewing,
  projectContentReview,
}) {
  const {
    division: {
      attributes: { pluralRAMSName, singularRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectReviews.ProjectContentReviewManager',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    currentUser,
    defaultProjectContentReviewFrequency,
    defaultProjectContentReviewInterval,
    isEditableHistory,
    projectContentReviewsAllowed,
    projectCreator,
    projectEndDate,
    projectId,
    reasonIsRequired,
    scheduledContentReviewRequest,
    suggestedDueDate,
  } = projectContentReview.data.attributes;

  const defaultReview = {
    isScheduling: null,
    isReviewing,
    dueDate:
      moment(suggestedDueDate).isValid() &&
      moment.parseZone(suggestedDueDate).toDate(),
    reason: '',
    assignedUser: {
      firstName: '',
      lastName: '',
      id: '',
    },
  };

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    ,
    handleRequestError,
  ] = useRequestError();

  const [
    currentReview,
    setCurrentReview,
    handleReviewInputChange,
    handleReviewOptionChange,
    ,
    handleReviewDateChange,
  ] = useForm(defaultReview);

  const [contentReviewSubmitting, setContentReviewSubmitting] = useState(false);

  const shoulDisplayCompleteReview =
    projectContentReviewsAllowed && scheduledContentReviewRequest;

  const shouldDisplayScheduleReview =
    (scheduledContentReviewRequest && currentReview.isReviewing) ||
    (!scheduledContentReviewRequest && projectContentReviewsAllowed);

  const shouldShowDateWarningMessage =
    currentReview.dueDate > moment.parseZone(projectEndDate) ||
    currentReview.dueDate > moment.parseZone(suggestedDueDate).toDate();

  const dateWarningMessage = () => {
    if (currentReview.dueDate > moment.parseZone(projectEndDate)) {
      return i18nT('dateWarnings.afterEndDate', {
        interpolation: { escapeValue: false },
        singularRAMSName,
      });
    } else if (
      currentReview.dueDate > moment.parseZone(suggestedDueDate).toDate()
    ) {
      return i18nT('dateWarnings.exceedsFrequency');
    }
  };

  const setProjectContentReviewParams = () => {
    const params = { schedule_option_selected: currentReview.isScheduling };

    if (currentReview.isReviewing && scheduledContentReviewRequest.data) {
      params.scheduled_content_review_request = {
        id: scheduledContentReviewRequest.data.id,
      };
    }
    if (currentReview.isScheduling === 'checked') {
      params.new_content_review_request = {
        due_date: moment.parseZone(currentReview.dueDate).format('DD/MM/YYYY'),
        assignee_id: currentReview.assignedUser.id,
      };
    }
    if (currentReview.reason) {
      params.new_history = { reason: currentReview.reason };
    }

    return { project_content_review: params };
  };

  const submitProjectContentReview = () => {
    setContentReviewSubmitting(true);
    axios
      .post(
        `/projects/${projectId}/project_content_reviews`,
        setProjectContentReviewParams(),
      )
      .then((response) => {
        if (response.status === 201) {
          window.location.assign(`/projects/${projectId}`);
        }
      })
      .catch(handleRequestError)
      .finally(() => {
        setContentReviewSubmitting(false);
      });
  };

  const fetchAssignableUsers = React.useCallback(
    _.debounce(
      (inputValue, callback) => {
        axios
          .get(`/users?search=${inputValue}`)
          .then((response) =>
            response.data.data.map((user) => ({
              label: personDisplayName({ attributes: user.attributes }),
              value: {
                firstName: user.attributes.firstName,
                lastName: user.attributes.lastName,
                id: user.id,
              },
            })),
          )
          .then(callback)
          .catch(console.error);
      },
      500,
      { maxWait: 1000 },
    ),
    [],
  );

  return (
    <>
      <div className='tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-10'>
        <ErrorMessage
          isFallback={requestError.isFallback}
          validationErrors={requestError.validationErrors}
          wrapperClassName='tw-mb-8 tw-w-94'
        />
        {isEditableHistory && (
          <div className='tw-mb-8 tw-w-94'>
            <TextArea
              additionalLabel={
                reasonIsRequired ?
                  i18nTShared('required')
                : i18nTShared('optional')
              }
              fieldError={requestError.validationErrors.reasonForEditing}
              label={i18nT('reasonForEditing.label')}
              name='reason'
              onChange={handleReviewInputChange}
              onFocus={() => {
                removeErrorStyling({ target: { name: 'reasonForEditing' } });
              }}
              value={currentReview.reason}
            />
          </div>
        )}
        {shoulDisplayCompleteReview && (
          <div className='tw-w-65'>
            <CheckboxField
              checked={currentReview.isReviewing}
              disabled={false}
              label={i18nT('completeReview.label')}
              name='isReviewing'
              onChange={handleReviewInputChange}
            />
            <div className='tw-mb-6 tw-pl-8 tw-text-s'>
              {scheduledContentReviewRequestDueIn({
                i18nT,
                reviewDueDate:
                  scheduledContentReviewRequest.data.attributes.dueDate,
              })}
            </div>
          </div>
        )}
        {shouldDisplayScheduleReview && (
          <div className={classNames(currentReview.isReviewing && 'tw-pl-8')}>
            <div className='tw-font-medium'>
              {i18nT('scheduleReview.title')}
            </div>
            <div className='tw-mt-1 tw-text-s'>
              {i18nT('scheduleReview.description', {
                frequency: formatDuration({
                  durationParts: {
                    [defaultProjectContentReviewInterval]:
                      defaultProjectContentReviewFrequency,
                  },
                }),
                interpolation: { escapeValue: false },
                pluralRAMSName: voca.capitalize(pluralRAMSName),
              })}
            </div>
            <div>
              <div className='tw-mb-4'>
                <RadioField
                  checked={currentReview.isScheduling === 'checked'}
                  label={i18nT('scheduleReview.scheduleNow')}
                  name='isScheduling'
                  onChange={() => {
                    setCurrentReview({
                      ...currentReview,
                      isScheduling: 'checked',
                    });
                  }}
                />
              </div>
              {currentReview.isScheduling === 'checked' && (
                <div className='tw-ml-7.5'>
                  <div className='tw-mb-6'>
                    <DatePickerField
                      earliestDate={new Date()}
                      fieldError={requestError.validationErrors.nextReviewDate}
                      label={i18nT('scheduleReview.nextReviewDate')}
                      name='dueDate'
                      onChange={handleReviewDateChange}
                      removeErrorStyling={() =>
                        removeErrorStyling({
                          target: { name: 'nextReviewDate' },
                        })
                      }
                      value={currentReview.dueDate}
                    />
                  </div>
                  <div className='tw-w-87'>
                    {shouldShowDateWarningMessage && (
                      <p className='tw-m-0 tw-mb-6 tw-mt-4 tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
                        {dateWarningMessage()}
                      </p>
                    )}
                  </div>
                  <div className='tw-mb-6'>
                    <span className='field__label tw-font-medium'>
                      {i18nTShared('assignedTo')}
                    </span>
                    <div className='tw-max-w-87 tw-mb-2'>
                      <Async
                        cacheOptions={true}
                        className={classNames(
                          'collection-select__select-container',
                          requestError.validationErrors.assignedUser
                            ?.fieldHighlighted && 'field__invalid',
                        )}
                        classNamePrefix='collection-select'
                        components={{
                          NoOptionsMessage,
                          ClearIndicator,
                          DropdownIndicator: () => null,
                          Option,
                        }}
                        isClearable={true}
                        loadOptions={fetchAssignableUsers}
                        name='assignedUser'
                        onChange={handleReviewOptionChange}
                        onFocus={() =>
                          removeErrorStyling({
                            target: { name: 'assignedUser' },
                          })
                        }
                        openMenuOnClick={false}
                        placeholder={i18nT('scheduleReview.searchPlaceholder')}
                        value={
                          currentReview.assignedUser.firstName && {
                            label: `${currentReview.assignedUser.firstName} ${currentReview.assignedUser.lastName}`,
                            value: { id: currentReview.assignedUser.id },
                          }
                        }
                      />
                    </div>
                    <span className='tw-flex'>
                      <span className='tw-font-normal'>
                        {i18nT('scheduleReview.assignTo')}{' '}
                        {AssignButton(currentUser, handleReviewOptionChange)}
                      </span>
                      {projectCreator && (
                        <span className='ws-pre-wrap tw-font-normal'>
                          {' '}
                          {i18nTShared('conjunctions.or')}{' '}
                          {AssignButton(
                            projectCreator,
                            handleReviewOptionChange,
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}
              <RadioField
                checked={currentReview.isScheduling === 'notChecked'}
                label={i18nT('scheduleReview.dontSchedule')}
                name='isScheduling'
                onChange={() =>
                  setCurrentReview({
                    ...currentReview,
                    isScheduling: 'notChecked',
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
      <ProjectContentReviewFooter
        anyUnsaved={false}
        contentReviewSubmitting={contentReviewSubmitting}
        disabled={submitDisabled}
        isReviewing={isReviewing}
        projectId={projectId}
        submitProjectContentReview={submitProjectContentReview}
      />
    </>
  );
}

ProjectContentReviewManager.propTypes = {
  isReviewing: PropTypes.bool.isRequired,
  projectContentReview: PropTypes.exact({
    data: PropTypes.exact({
      attributes: PropTypes.exact({
        defaultProjectContentReviewFrequency: PropTypes.number,
        defaultProjectContentReviewInterval: PropTypes.oneOf([
          'days',
          'weeks',
          'months',
          'years',
        ]),
        suggestedDueDate: PropTypes.string,
        scheduledContentReviewRequest: PropTypes.exact({
          data: PropTypes.exact({
            id: PropTypes.string.isRequired,
            type: PropTypes.string,
            attributes: PropTypes.exact({
              dueDate: PropTypes.string.isRequired,
            }),
          }),
        }),
        projectCreator: PropTypes.object,
        currentUser: PropTypes.exact({
          data: PropTypes.exact({
            id: PropTypes.string.isRequired,
            type: PropTypes.string,
            attributes: PropTypes.exact({
              deletedAt: PropTypes.string,
              email: PropTypes.string,
              firstName: PropTypes.string.isRequired,
              lastName: PropTypes.string,
            }),
            meta: PropTypes.object,
          }),
        }),
        isEditableHistory: PropTypes.bool.isRequired,
        reasonIsRequired: PropTypes.bool.isRequired,
        projectContentReviewsAllowed: PropTypes.bool.isRequired,
        projectId: PropTypes.number.isRequired,
        projectEndDate: PropTypes.string,
      }),
      id: PropTypes.string.isRequired,
      meta: PropTypes.object,
      type: PropTypes.string,
    }),
  }),
};
